"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import ProductGrid from "./ProductGrid";
import FilterBar from "./FilterBar";
import { decodeSku } from "@/lib/utils/decodeSku";

import type { Product } from "@/types/product";
import type { Category } from "@/types/category";
import { getProductsAction } from "@/server/shop/products.action";

type Props = {
    initialProducts: Product[];
    currentCategoryId: number;
    totalItems: number;
    filteredTotal: number;
    categories: Category[];
    showCategory: boolean;
    activeFilters?: string[];
};

export default function ProductInfinite({
                                            initialProducts,
                                            currentCategoryId,
                                            totalItems,
                                            filteredTotal,
                                            categories,
                                            showCategory: initialShowCategory,
                                            activeFilters
                                        }: Props) {

    const [products, setProducts] = useState<Product[]>(initialProducts);
    const [page, setPage] = useState(2);
    const [loading, setLoading] = useState(false);
    const [allLoaded, setAllLoaded] = useState(
        initialProducts.length >= filteredTotal
    );

    const [sort, setSort] = useState<"part" | "newest">("part");
    const [showCategory, setShowCategory] = useState(initialShowCategory);
    const [showAll, setShowAll] = useState(false);
    const [showProductSkuDecoded, setShowProductSkuDecoded] = useState(false);

    const fetchTriggerRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const firstRender = useRef(true);
    const [refreshing, setRefreshing] = useState(false);

    // reset وقتی initialProducts (فیلتر/مسیر) عوض می‌شه
    useEffect(() => {
        setProducts(initialProducts);
        setPage(2);
        setAllLoaded(initialProducts.length >= filteredTotal);
        firstRender.current = true;
    }, [initialProducts, filteredTotal]);

    const loadFirstPage = useCallback(async () => {
        setRefreshing(true);
        try {
            const params = new URLSearchParams({
                category_id: String(currentCategoryId),
                page: "1",
                limit: "20",
                sort: sort === "newest" ? "newest" : "sku",
            });
            if (showAll) params.append("public", "yes");

            const data = await getProductsAction(
                currentCategoryId,
                1,
                20,
                sort === "newest" ? "newest" : "sku",
                showAll
            );
            let newProducts = data.data || [];

            if (activeFilters && activeFilters.length > 0) {
                newProducts = newProducts.filter((p) =>
                    decodeSku(p.sku).parts.some(
                        (part) => activeFilters.includes(part.key.toLowerCase())
                    )
                );
            }

            setProducts(newProducts);
            setPage(2);
            const isLastServerPage = data.data?.length < 20;
            setAllLoaded(isLastServerPage || newProducts.length >= filteredTotal);
        } finally {
            setRefreshing(false);
            firstRender.current = false;
        }
    }, [currentCategoryId, sort, showAll, activeFilters, filteredTotal]);

    // فقط وقتی sort یا showAll عوض می‌شه reload کن (نه بار اول mount)
    const isFirstSortRender = useRef(true);
    const isFirstShowAllRender = useRef(true);

    useEffect(() => {
        if (isFirstSortRender.current) {
            isFirstSortRender.current = false;
            return;
        }
        loadFirstPage();
    }, [sort]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isFirstShowAllRender.current) {
            isFirstShowAllRender.current = false;
            return;
        }
        loadFirstPage();
    }, [showAll]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchProducts = useCallback(
        async (pageNumber: number) => {
            if (loading || allLoaded) return;
            try {
                setLoading(true);
                const LIMIT = 20;
                const params = new URLSearchParams({
                    category_id: String(currentCategoryId),
                    page: String(pageNumber),
                    limit: String(LIMIT),
                    sort: sort === "newest" ? "newest" : "sku",
                });
                if (showAll) params.append("public", "yes");

                const data = await getProductsAction(
                    currentCategoryId,
                    pageNumber,
                    20,
                    sort === "newest" ? "newest" : "sku",
                    showAll
                );
                const rawProducts = data.data || [];

                const isLastServerPage = rawProducts.length < LIMIT;

                let filtered = rawProducts;
                if (activeFilters && activeFilters.length > 0) {
                    filtered = rawProducts.filter((p) =>
                        activeFilters.every((f) =>
                            decodeSku(p.sku).parts.some(
                                (part) => part.key.toLowerCase() === f
                            )
                        )
                    );
                }

                setProducts((prev) => {
                    const ids = new Set(prev.map((p) => p.id));
                    const merged = [...prev, ...filtered.filter((p) => !ids.has(p.id))];
                    if (isLastServerPage || merged.length >= filteredTotal) {
                        setAllLoaded(true);
                    }
                    return merged;
                });

                if (isLastServerPage) {
                    setAllLoaded(true);
                }

            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        },
        [currentCategoryId, sort, showAll, loading, allLoaded, activeFilters, filteredTotal]
    );

    // infinite scroll
    useEffect(() => {
        const el = fetchTriggerRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loading && !allLoaded) {
                    fetchProducts(page).then(() => setPage((p) => p + 1));
                }
            },
            { rootMargin: "800px", threshold: 0.01 }
        );

        observer.observe(el);
        observerRef.current = observer;
        return () => observer.disconnect();
    }, [fetchProducts, page, loading, allLoaded]);

    const displayTotal = activeFilters ? products.length : totalItems;

    return (
        <>
            <FilterBar
                sort={sort}
                setSort={setSort}
                totalProducts={products.length}
                showCategory={showCategory}
                setShowCategory={setShowCategory}
                totalItems={displayTotal}
                showAll={showAll}
                setShowAll={setShowAll}
                showProductSkuDecoded={showProductSkuDecoded}
                setShowProductSkuDecoded={setShowProductSkuDecoded}
            />

            <div className={`transition-all duration-300 ${refreshing ? "blur-sm opacity-50 pointer-events-none" : ""}`}>
                <ProductGrid
                    products={products}
                    categories={categories}
                    showCategory={showCategory}
                    showProductSkuDecoded={showProductSkuDecoded}
                />
            </div>

            {!allLoaded && (
                <div className="py-10 flex justify-center">
                    {loading && (
                        <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin " />
                    )}
                    <div ref={fetchTriggerRef} />
                </div>
            )}

            {allLoaded && products.length > 0 && (
                <div className="text-center py-10 text-gray-400">
                    محصول بیشتری وجود ندارد
                </div>
            )}

            {allLoaded && products.length === 0 && (
                <div className="text-center py-10 text-gray-400">
                    محصولی با این فیلتر یافت نشد
                </div>
            )}
        </>
    );
}
